import { Fragment, useState, useRef, useEffect } from 'react';
import moment from 'moment';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useTheme } from '../utils/theme-context';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { tryToFetchNotificationsSidebar } from '../store/brokers/partners/notifications/notificationsSlice';
import Navigation from './layout-components/Navigation';
import Sidebar from './layout-components/Sidebar';
import Menu from './layout-components/Menu';
import NotificationsModal from '../components/shared/notifications/NotificationsModal';
import LastUpdated from '../components/shared/last-updated/LastUpdated';
import { PromptsProvider } from '../features/dashboard/prompts/PromptsContext';

const Layout = () => {
    const dispatch = useAppDispatch();
    const { theme } = useTheme();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showNotificationsModal, setShowNotificationsModal] = useState<boolean>(false);
    const [unreadNotifications, setUnreadNotifications] = useState<any>()
    const notificationRef = useRef<any>(null);
    const notificationsData = useAppSelector((state) => state.notifications)

    useEffect(() => {
        setUnreadNotifications(notificationsData?.notificationsSidebar?.data?.elements)
    }, [notificationsData])

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (notificationRef.current && !notificationRef.current?.contains(event.target as Node)) {
                setShowNotifications(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const onGettingData = async () => {
        try {
            const data: any = {
                pageSize: 5,
                pageNumber: 1,
            }
            const notificationsData = {
                ...data || {},
                data: {
                    status: 'unread'
                }
            }
            await dispatch(tryToFetchNotificationsSidebar(notificationsData)).unwrap();
        } catch (err) {
            console.error(err)
        }
    }

    useEffect(() => {
        onGettingData();
    }, [])

    const onClickShowNotificationssModals = () => {
        setShowNotificationsModal(!showNotificationsModal)
        setShowNotifications(false)
    }

    const formatNotificationsTitle = (title: string) => {
        return title
            .replace(/_/g, ' ')
            .replace(/\w\S*/g, word =>
                word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            );
    }
    const { pathname } = useLocation();
    const hideMenu = /^.*\/costs(\/[^/]+)?$/.test(pathname)
        || /^.*\/need-help(\/[^/]+)?$/.test(pathname)
        || /^.*\/tips-feedback(\/[^/]+)?$/.test(pathname)
        || /^.*\/subscriptions(\/[^/]+)?$/.test(pathname)
        || /^.*\/comparison-boards(\/[^/]+)?$/.test(pathname)
        || /^.*\/leo-chat(\/[^/]+)?$/.test(pathname)
        || /^.*\/expert-partners(\/[^/]+)?$/.test(pathname);
    return (
        <div className='bg-white dark:bg-bgColor-darkMode flex flex-col min-h-screen'>
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog
                    as='div'
                    className='relative z-40 md:hidden'
                    onClose={setSidebarOpen}
                >
                    <Transition.Child
                        as={Fragment}
                        enter='transition-opacity ease-linear duration-300'
                        enterFrom='opacity-0'
                        enterTo='opacity-100'
                        leave='transition-opacity ease-linear duration-300'
                        leaveFrom='opacity-100'
                        leaveTo='opacity-0'
                    >
                        <div className='fixed inset-0 bg-gray-600 bg-opacity-75' />
                    </Transition.Child>

                    <div className='fixed inset-0 flex z-40'>
                        <Transition.Child
                            as={Fragment}
                            enter='transition ease-in-out duration-300 transform'
                            enterFrom='-translate-x-full'
                            enterTo='translate-x-0'
                            leave='transition ease-in-out duration-300 transform'
                            leaveFrom='translate-x-0'
                            leaveTo='-translate-x-full'
                        >
                            <Dialog.Panel className='relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white'>
                                <Transition.Child
                                    as={Fragment}
                                    enter='ease-in-out duration-300'
                                    enterFrom='opacity-0'
                                    enterTo='opacity-100'
                                    leave='ease-in-out duration-300'
                                    leaveFrom='opacity-100'
                                    leaveTo='opacity-0'
                                >
                                    <div className='absolute top-0 right-0 -mr-12 pt-2'>
                                        <button
                                            type='button'
                                            className='ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white'
                                            onClick={() => setSidebarOpen(false)}
                                        >
                                            <span className='sr-only'>Close sidebar</span>
                                            <img src='/assets/shared/close-grey.svg' alt='close' />
                                            <span className='h-6 w-6 text-white' aria-hidden='true' />
                                        </button>
                                    </div>
                                </Transition.Child>
                                <div className='flex-shrink-0 flex items-center px-4 mb-2 md:mb-10'>
                                    <NavLink to='/'>
                                        <img className='max-h-8 w-auto' src='/svg/layout/hotel-avatar.svg' alt='logo' />
                                    </NavLink>
                                </div>
                                <div className='mt-5 flex-1 h-0 overflow-y-auto'>
                                    <nav className='px-4 md:px-2 space-y-1'>
                                        <Navigation closeSidebar={() => setSidebarOpen(false)} />
                                    </nav>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                        <div className='flex-shrink-0 w-14' aria-hidden='true'></div>
                    </div>
                </Dialog>
            </Transition.Root>
            <Sidebar />

            <div className='flex flex-col flex-1 md:pl-64'>
                <div className='p-4'>
                    <header className='w-full flex flex-wrap items-center justify-between border-b border-gray-100 pb-4'>
                        <div>
                            <h2 className='text-gray-900 dark:text-white text-xl md:text-3xl font-semibold'>Account Management Dashboard</h2>
                            <p className='text-gray-500 dark:text-white text-sm'>Monitor agent performance, client satisfaction and usage analytics</p>
                        </div>
                        <div className='flex flex-wrap justify-end'>
                            <LastUpdated />
                            <div className='ml-auto flex items-center gap-4 relative' ref={notificationRef}>
                                <button
                                    onClick={() => setShowNotifications(!showNotifications)}
                                    className='relative p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 ml-2'
                                >
                                    <img src={theme === 'dark' ? '/assets/chat/bell-white.svg' : '/assets/chat/bell.svg'} alt='notifications' className='w-5 h-5' />
                                    {(unreadNotifications?.length > 0) && (
                                        <span className='absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-red-600 rounded-full'>
                                            {unreadNotifications?.length}
                                        </span>
                                    )}
                                </button>
                                {showNotifications && (
                                    <div className='absolute right-2 top-10 mt-2 w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50'>
                                        <div className='p-4'>
                                            <h4 className='text-sm font-semibold text-gray-700 dark:text-gray-200'>Unread Notifications</h4>
                                        </div>
                                        <ul className='max-h-60 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800'>
                                            {unreadNotifications?.length > 0 && unreadNotifications?.map((item: any) => {
                                                const timeAgo = moment(item?.createdAt).fromNow();
                                                return (
                                                    <li key={item?._id} className='px-4 py-2 text-sm text-gray-800 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'>
                                                        <p className='dark:text-primary-darkMode'>{formatNotificationsTitle(item?.action || '')}</p>
                                                        <p>{timeAgo}</p>
                                                    </li>
                                                )
                                            })}
                                        </ul>
                                        <div className='p-2 text-center border-t border-gray-100 dark:border-gray-800'>
                                            <button
                                                onClick={onClickShowNotificationssModals}
                                                className='text-sm text-primary font-medium hover:underline dark:text-primary-darkMode'>
                                                Show all
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </header>
                    {!hideMenu && <Menu />}
                    <main className='flex-1 px-5 py-4 w-full'>
                        <PromptsProvider>
                            <Outlet />
                        </PromptsProvider>
                    </main>
                </div>
                {showNotificationsModal &&
                    <NotificationsModal
                        openModal={showNotificationsModal}
                        handleCloseModal={onClickShowNotificationssModals}
                    />
                }
            </div>
        </div>
    );
};

export default Layout;
